import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { CalculatorTotals } from "@/hooks/useOrdersCalculator";
import { formatMonetaryAmount } from "@/utils/numbers.utils";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface OrdersCalculatorFooterProps {
  totals: CalculatorTotals;
  selectedCount: number;
  onCancel: () => void;
}

interface AmountDisplayProps {
  label: string;
  amount: number;
  currency: string;
  isLarge?: boolean;
}

function AmountDisplay({ label, amount, currency, isLarge = false }: AmountDisplayProps) {
  const formattedAmount = formatMonetaryAmount(amount);

  return (
    <div className="flex flex-col flex-1 min-w-0 text-center">
      <span className="text-xs text-muted-foreground mb-0.5 font-medium">{label}</span>
      <span className={`tabular-nums ${isLarge ? "text-lg font-bold" : "text-sm font-semibold"}`}>
        <CurrencyAmount amount={formattedAmount} currency={currency} />
      </span>
    </div>
  );
}

interface CurrencyTotalsCardProps {
  currency: string;
  total: number;
  bill: number;
  tip: number;
}

function CurrencyTotalsCard({ currency, total, bill, tip }: CurrencyTotalsCardProps) {
  const { t } = useTranslation();

  return (
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 p-3 border border-primary/20">
      <div className="flex items-center justify-around gap-2">
        <AmountDisplay
          label={t("orders.calculator.total")}
          amount={total}
          currency={currency}
          isLarge
        />
        <div className="h-10 w-px bg-border" />
        <AmountDisplay
          label={t("orders.calculator.bill")}
          amount={bill}
          currency={currency}
        />
        <div className="h-10 w-px bg-border" />
        <AmountDisplay
          label={t("orders.calculator.tip")}
          amount={tip}
          currency={currency}
        />
      </div>
    </div>
  );
}

interface SelectionHeaderProps {
  selectedCount: number;
  onCancel: () => void;
}

function SelectionHeader({ selectedCount, onCancel }: SelectionHeaderProps) {
  const { t } = useTranslation();
  const hasSelections = selectedCount > 0;

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
        <span className="font-semibold text-sm">
          {hasSelections
            ? t("orders.calculator.ordersSelected", { count: selectedCount })
            : t("orders.calculator.noOrders")}
        </span>
      </div>
      <Button 
        variant="text" 
        size="sm" 
        onClick={onCancel} 
        className="h-8 rounded-full flex items-center gap-1.5 -mr-2"
      >
        <X className="h-4 w-4" />
        {t("orders.calculator.cancel")}
      </Button>
    </div>
  );
}

export function OrdersCalculatorFooter({
  totals,
  selectedCount,
  onCancel,
}: OrdersCalculatorFooterProps) {
  const currencies = Object.keys(totals);
  const hasSelections = selectedCount > 0;

  return (
    <div className="fixed bottom-20 left-0 right-0 z-50 px-3">
      <div className="max-w-2xl xl:max-w-md xl:px-[0.5rem] mx-auto">
        <Card className="rounded-2xl border border-border/50 shadow-xl bg-background">
          <div className="p-3.5 space-y-2.5">
            <SelectionHeader selectedCount={selectedCount} onCancel={onCancel} />

            {hasSelections && (
              <div className="space-y-2">
                {currencies.map((currency) => (
                  <CurrencyTotalsCard
                    key={currency}
                    currency={currency}
                    total={totals[currency].total}
                    bill={totals[currency].bill}
                    tip={totals[currency].tip}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
